/**
 * The contests on one Priority List (migration 969), and what the signed-in
 * person may do about them. Built on `listContestsForList` and
 * `getListWorkspaceId` (src/lib/contestService.ts) and `useWorkspaceMembers`,
 * which puts names to ids and answers "is this person a member of the list's
 * workspace", the same question the act `contest_priority_list` asks.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWorkspaceMembers } from './useWorkspaceMembers';
import {
  getListWorkspaceId,
  isContestableListState,
  listContestsForList,
  type ContestView,
} from '../lib/contestService';

export interface PriorityListContestsState {
  contests: ContestView[] | null;
  openContests: ContestView[];
  /** The signed-in person's own open contest on this list, if any. */
  myOpenContest: ContestView | null;
  /** True when the "Contest" control may be drawn for the signed-in person. */
  mayContest: boolean;
  /** Puts a name to a user id, from the list's workspace. */
  nameOf: (userId: string | null | undefined) => string;
  error: string | null;
  reload: () => Promise<void>;
}

/** The contests on one list, and what the signed-in person may do about them. */
export function usePriorityListContests(
  listId: string | null | undefined,
  listState: string | null | undefined,
  userId: string | null | undefined,
): PriorityListContestsState {
  const [contests, setContests] = useState<ContestView[] | null>(null);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { members, nameOf } = useWorkspaceMembers(workspaceId);

  const reload = useCallback(async () => {
    if (!listId) {
      setContests(null);
      return;
    }
    try {
      setContests(await listContestsForList(listId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [listId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    let cancelled = false;
    if (!listId) {
      setWorkspaceId(null);
      return;
    }
    void getListWorkspaceId(listId).then((ws) => {
      if (!cancelled) setWorkspaceId(ws);
    });
    return () => {
      cancelled = true;
    };
  }, [listId]);

  const openContests = useMemo(() => (contests ?? []).filter((c) => c.is_open), [contests]);
  const myOpenContest = useMemo(
    () => (userId ? openContests.find((c) => c.contested_by === userId) ?? null : null),
    [openContests, userId],
  );
  // Membership as the act reads it: a membership row in the list's workspace.
  const isMember = !!userId && members.some((m) => m.userId === userId);
  const mayContest = !!listId && isContestableListState(listState) && isMember && contests !== null && !myOpenContest;

  return { contests, openContests, myOpenContest, mayContest, nameOf, error, reload };
}
